import {api} from "../../api.ts";
import {showToast} from "../Notifications/notificationsSlice.tsx";
import {
    CreateEnsAlertRequest,
    EnsAlertDetails,
    EnsAlertListFilter,
    EnsAlertNotificationsResponse,
    EnsAlertPreset,
    EnsAlertStats,
    EnsAlertSummary,
    EnsAlertTemplate,
    EnsConfiguration,
    EnsMyPendingAlert,
    EnsMyAlert,
    EnsRecipientStatus,
    EnsTtsRequest,
    EnsUploadAudioResponse,
    EnsUserContact,
    EnsUserContactListItem,
    EnsVoicePrompt,
    PageResponse,
    PaginationArgs,
    SaveEnsAlertTemplateRequest,
    UpdateEnsConfigurationRequest,
} from "./types";

interface ListAlertsArgs extends PaginationArgs, EnsAlertListFilter {}

interface ListMyAlertsArgs extends PaginationArgs {
    recipientStatus?: EnsRecipientStatus;
}

interface ListUserContactsArgs extends PaginationArgs {
    phrase?: string;
}

interface UpsertUserContactArgs {
    userId: number;
    externalMsisdn: number | null;
    ensEligible: boolean;
}

interface OverrideConfirmArgs {
    publicId: string;
    userId: number;
}

interface UpdateTemplateArgs {
    id: number;
    body: SaveEnsAlertTemplateRequest;
}

interface ListMyAlertNotificationsArgs {
    afterNotificationId?: number;
    limit?: number;
    maxAgeHours?: number;
}

const appendListParam = (params: URLSearchParams, key: string, values?: string[]) => {
    values?.forEach((value) => params.append(key, value));
};

const buildAlertQuery = (args: ListAlertsArgs) => {
    const params = new URLSearchParams();
    if (args.offset != null) params.set('offset', String(args.offset));
    if (args.limit != null) params.set('limit', String(args.limit));
    if (args.phrase) params.set('phrase', args.phrase);
    appendListParam(params, 'statuses', args.statuses);
    appendListParam(params, 'types', args.types);
    if (args.triggeredFrom) params.set('triggeredFrom', args.triggeredFrom);
    if (args.triggeredTo) params.set('triggeredTo', args.triggeredTo);
    return params.toString();
};

const withQuery = (url: string, query: string) => query ? `${url}?${query}` : url;

const buildContactQuery = (args: ListUserContactsArgs) => {
    const params = new URLSearchParams();
    if (args.offset != null) params.set('offset', String(args.offset));
    if (args.limit != null) params.set('limit', String(args.limit));
    if (args.phrase) params.set('phrase', args.phrase);
    return params.toString();
};

export const ensApi = api.injectEndpoints({
    endpoints: (builder) => ({
        listEnsAlerts: builder.query<PageResponse<EnsAlertSummary>, ListAlertsArgs>({
            query: (args) => ({
                url: withQuery('/api/v1/ens/alerts', buildAlertQuery(args)),
            }),
            transformResponse: (response: { data: EnsAlertSummary[] }, meta) => {
                const totalCount = meta?.response?.headers.get('x-total-count');
                return {
                    data: response.data ?? [],
                    totalCount: totalCount ? parseInt(totalCount, 10) : (response.data ?? []).length,
                };
            },
            providesTags: (result) =>
                result
                    ? [
                        ...result.data.map((a) => ({type: 'EnsAlert' as const, id: a.publicId})),
                        {type: 'EnsAlerts' as const, id: 'LIST'},
                    ]
                    : [{type: 'EnsAlerts' as const, id: 'LIST'}],
        }),
        getEnsAlert: builder.query<EnsAlertDetails, string>({
            query: (publicId) => ({
                url: `/api/v1/ens/alerts/${publicId}`,
            }),
            transformResponse: (response: { data: EnsAlertDetails }) => response.data,
            providesTags: (_, __, publicId) => [{type: 'EnsAlert', id: publicId}],
        }),
        getEnsStats: builder.query<EnsAlertStats, { days?: number }>({
            query: ({days}) => ({
                url: '/api/v1/ens/alerts/stats',
                params: days != null ? {days} : undefined,
            }),
            transformResponse: (response: { data: EnsAlertStats }) => response.data,
            providesTags: [{type: 'EnsStats', id: 'GLOBAL'}],
        }),
        getEnsPresets: builder.query<EnsAlertPreset[], void>({
            query: () => ({
                url: '/api/v1/ens/alerts/presets',
            }),
            transformResponse: (response: { data: EnsAlertPreset[] }) => response.data ?? [],
            providesTags: [{type: 'EnsPresets', id: 'GLOBAL'}],
        }),
        listEnsAlertTemplates: builder.query<EnsAlertTemplate[], void>({
            query: () => ({
                url: '/api/v1/ens/alert-templates',
            }),
            transformResponse: (response: { data: EnsAlertTemplate[] }) => response.data ?? [],
            providesTags: [{type: 'EnsTemplates', id: 'LIST'}],
        }),
        createEnsAlertTemplate: builder.mutation<EnsAlertTemplate, SaveEnsAlertTemplateRequest>({
            query: (body) => ({
                url: '/api/v1/ens/alert-templates',
                method: 'POST',
                body,
            }),
            transformResponse: (response: { data: EnsAlertTemplate }) => response.data,
            invalidatesTags: [{type: 'EnsTemplates', id: 'LIST'}],
            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;
                    dispatch(showToast({description: 'Template saved', type: 'success'}));
                } catch {
                    dispatch(showToast({description: 'Failed to save template', type: 'error'}));
                }
            },
        }),
        createEnsAlertTemplateFromAlert: builder.mutation<EnsAlertTemplate, string>({
            query: (publicId) => ({
                url: `/api/v1/ens/alert-templates/from-alert/${publicId}`,
                method: 'POST',
            }),
            transformResponse: (response: { data: EnsAlertTemplate }) => response.data,
            invalidatesTags: [{type: 'EnsTemplates', id: 'LIST'}],
            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;
                    dispatch(showToast({description: 'Template saved from alert', type: 'success'}));
                } catch {
                    dispatch(showToast({description: 'Failed to save template from alert', type: 'error'}));
                }
            },
        }),
        updateEnsAlertTemplate: builder.mutation<EnsAlertTemplate, UpdateTemplateArgs>({
            query: ({id, body}) => ({
                url: `/api/v1/ens/alert-templates/${id}`,
                method: 'PUT',
                body,
            }),
            transformResponse: (response: { data: EnsAlertTemplate }) => response.data,
            invalidatesTags: [{type: 'EnsTemplates', id: 'LIST'}],
            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;
                    dispatch(showToast({description: 'Template updated', type: 'success'}));
                } catch {
                    dispatch(showToast({description: 'Failed to update template', type: 'error'}));
                }
            },
        }),
        deleteEnsAlertTemplate: builder.mutation<void, number>({
            query: (id) => ({
                url: `/api/v1/ens/alert-templates/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: [{type: 'EnsTemplates', id: 'LIST'}],
            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;
                    dispatch(showToast({description: 'Template deleted', type: 'success'}));
                } catch {
                    dispatch(showToast({description: 'Failed to delete template', type: 'error'}));
                }
            },
        }),
        listMyEnsAlerts: builder.query<PageResponse<EnsMyAlert>, ListMyAlertsArgs>({
            query: (params) => ({
                url: '/api/v1/ens/alerts/me',
                params,
            }),
            transformResponse: (response: {data: EnsMyAlert[]}, meta) => {
                const totalCount = meta?.response?.headers.get('x-total-count');
                return {
                    data: response.data,
                    totalCount: totalCount ? parseInt(totalCount, 10) : response.data.length,
                };
            },
            providesTags: [{type: 'EnsMyAlerts', id: 'LIST'}],
        }),
        listMyPendingEnsAlerts: builder.query<EnsMyPendingAlert[], void>({
            query: () => ({
                url: '/api/v1/ens/alerts/me/pending',
            }),
            transformResponse: (response: { data: EnsMyPendingAlert[] }) => response.data ?? [],
            providesTags: [{type: 'EnsMyAlerts', id: 'LIST'}],
        }),
        listMyEnsAlertNotifications: builder.query<
            EnsAlertNotificationsResponse,
            ListMyAlertNotificationsArgs
        >({
            query: ({afterNotificationId, limit, maxAgeHours}) => ({
                url: '/api/v1/ens/alerts/me/notifications',
                params: {
                    ...(afterNotificationId != null ? {afterNotificationId} : {}),
                    ...(limit != null ? {limit} : {}),
                    ...(maxAgeHours != null ? {maxAgeHours} : {}),
                },
            }),
            transformResponse: (response: EnsAlertNotificationsResponse) => ({
                data: response.data ?? [],
                nextAfterNotificationId: response.nextAfterNotificationId,
                hasMore: response.hasMore ?? false,
            }),
        }),
        createEnsAlert: builder.mutation<EnsAlertDetails, CreateEnsAlertRequest>({
            query: (body) => ({
                url: '/api/v1/ens/alerts',
                method: 'POST',
                body,
            }),
            transformResponse: (response: { data: EnsAlertDetails }) => response.data,
            invalidatesTags: [
                {type: 'EnsAlerts', id: 'LIST'},
                {type: 'EnsStats', id: 'GLOBAL'},
            ],
            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;
                    dispatch(showToast({description: 'Alert created successfully', type: 'success'}));
                } catch {
                    dispatch(showToast({description: 'Failed to create alert', type: 'error'}));
                }
            },
        }),
        confirmEnsAlert: builder.mutation<void, string>({
            query: (publicId) => ({
                url: `/api/v1/ens/alerts/${publicId}/confirm`,
                method: 'POST',
            }),
            invalidatesTags: (_, __, publicId) => [
                {type: 'EnsAlert', id: publicId},
                {type: 'EnsAlerts', id: 'LIST'},
                {type: 'EnsMyAlerts', id: 'LIST'},
                {type: 'EnsStats', id: 'GLOBAL'},
            ],
            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;
                    dispatch(showToast({description: 'Alert confirmed', type: 'success'}));
                } catch {
                    dispatch(showToast({description: 'Failed to confirm alert', type: 'error'}));
                }
            },
        }),
        cancelEnsAlert: builder.mutation<void, string>({
            query: (publicId) => ({
                url: `/api/v1/ens/alerts/${publicId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_, __, publicId) => [
                {type: 'EnsAlert', id: publicId},
                {type: 'EnsAlerts', id: 'LIST'},
                {type: 'EnsMyAlerts', id: 'LIST'},
                {type: 'EnsStats', id: 'GLOBAL'},
            ],
            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;
                    dispatch(showToast({description: 'Alert cancelled', type: 'success'}));
                } catch {
                    dispatch(showToast({description: 'Failed to cancel alert', type: 'error'}));
                }
            },
        }),
        overrideConfirmEnsAlert: builder.mutation<void, OverrideConfirmArgs>({
            query: ({publicId, userId}) => ({
                url: `/api/v1/ens/alerts/${publicId}/recipients/override-confirm`,
                method: 'POST',
                body: {userId},
            }),
            invalidatesTags: (_, __, {publicId}) => [
                {type: 'EnsAlert', id: publicId},
                {type: 'EnsAlerts', id: 'LIST'},
                {type: 'EnsMyAlerts', id: 'LIST'},
                {type: 'EnsStats', id: 'GLOBAL'},
            ],
            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;
                    dispatch(showToast({description: 'Recipient confirmed by admin', type: 'success'}));
                } catch {
                    dispatch(showToast({description: 'Failed to override-confirm recipient', type: 'error'}));
                }
            },
        }),
        getEnsConfiguration: builder.query<EnsConfiguration, void>({
            query: () => ({
                url: '/api/v1/ens/configuration',
            }),
            transformResponse: (response: { data: EnsConfiguration }) => response.data,
            providesTags: [{type: 'EnsConfig', id: 'GLOBAL'}],
        }),
        updateEnsConfiguration: builder.mutation<EnsConfiguration, UpdateEnsConfigurationRequest>({
            query: (body) => ({
                url: '/api/v1/ens/configuration',
                method: 'PUT',
                body,
            }),
            transformResponse: (response: { data: EnsConfiguration }) => response.data,
            invalidatesTags: [{type: 'EnsConfig', id: 'GLOBAL'}],
            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;
                    dispatch(showToast({description: 'Configuration updated', type: 'success'}));
                } catch {
                    dispatch(showToast({description: 'Failed to update configuration', type: 'error'}));
                }
            },
        }),
        getEnsUserContact: builder.query<EnsUserContact | null, number>({
            query: (userId) => ({
                url: `/api/v1/ens/user-contacts/${userId}`,
            }),
            transformResponse: (response: { data: EnsUserContact | null }) => response.data,
            providesTags: (_, __, userId) => [{type: 'EnsContact', id: userId}],
        }),
        upsertEnsUserContact: builder.mutation<EnsUserContact, UpsertUserContactArgs>({
            query: ({userId, externalMsisdn, ensEligible}) => ({
                url: `/api/v1/ens/user-contacts/${userId}`,
                method: 'PUT',
                body: {externalMsisdn, ensEligible},
            }),
            transformResponse: (response: { data: EnsUserContact }) => response.data,
            invalidatesTags: (_, __, {userId}) => [
                {type: 'EnsContact', id: userId},
                {type: 'EnsContacts', id: 'LIST'},
            ],
            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;
                    dispatch(showToast({description: 'Emergency contact saved', type: 'success'}));
                } catch {
                    dispatch(showToast({description: 'Failed to save emergency contact', type: 'error'}));
                }
            },
        }),
        listEnsUserContacts: builder.query<PageResponse<EnsUserContactListItem>, ListUserContactsArgs>({
            query: (args) => ({
                url: withQuery('/api/v1/ens/user-contacts', buildContactQuery(args)),
            }),
            transformResponse: (response: { data: EnsUserContactListItem[] }, meta) => {
                const totalCount = meta?.response?.headers.get('x-total-count');
                return {
                    data: response.data ?? [],
                    totalCount: totalCount ? parseInt(totalCount, 10) : (response.data ?? []).length,
                };
            },
            providesTags: (result) =>
                result
                    ? [
                        ...result.data.map((c) => ({type: 'EnsContact' as const, id: c.userId})),
                        {type: 'EnsContacts' as const, id: 'LIST'},
                    ]
                    : [{type: 'EnsContacts' as const, id: 'LIST'}],
        }),
        uploadEnsAudioPrompt: builder.mutation<EnsUploadAudioResponse, FormData>({
            query: (data) => ({
                url: '/api/v1/files',
                method: 'POST',
                body: data,
            }),
        }),
        generateEnsTts: builder.mutation<EnsVoicePrompt, EnsTtsRequest>({
            query: (body) => ({
                url: '/api/v1/ens/voice-prompts/tts',
                method: 'PUT',
                body,
            }),
            transformResponse: (response: EnsVoicePrompt | { data: EnsVoicePrompt }) =>
                'data' in response ? response.data : response,
            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;
                } catch {
                    dispatch(showToast({description: 'Failed to save voice prompt', type: 'error'}));
                }
            },
        }),
        previewEnsTts: builder.mutation<{ url: string; type: string }, EnsTtsRequest>({
            query: (body) => ({
                url: '/api/v1/tts-speech',
                method: 'POST',
                body,
                responseHandler: async (response: Response) => {
                    const buffer = await response.arrayBuffer();
                    return {buffer, headers: response.headers};
                },
            }),
            transformResponse: (response: { buffer: ArrayBuffer; headers: Headers }) => {
                const type = response.headers.get('content-type') || 'audio/mpeg';
                const blob = new Blob([response.buffer], {type});
                return {url: URL.createObjectURL(blob), type};
            },
            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled;
                } catch {
                    dispatch(showToast({description: 'Failed to preview voice prompt', type: 'error'}));
                }
            },
        }),
    }),
});

export const {
    useListEnsAlertsQuery,
    useLazyListEnsAlertsQuery,
    useGetEnsAlertQuery,
    useGetEnsStatsQuery,
    useGetEnsPresetsQuery,
    useListEnsAlertTemplatesQuery,
    useCreateEnsAlertTemplateMutation,
    useCreateEnsAlertTemplateFromAlertMutation,
    useUpdateEnsAlertTemplateMutation,
    useDeleteEnsAlertTemplateMutation,
    useListMyPendingEnsAlertsQuery,
    useListMyEnsAlertsQuery,
    useLazyListMyEnsAlertNotificationsQuery,
    useCreateEnsAlertMutation,
    useConfirmEnsAlertMutation,
    useCancelEnsAlertMutation,
    useOverrideConfirmEnsAlertMutation,
    useGetEnsConfigurationQuery,
    useUpdateEnsConfigurationMutation,
    useGetEnsUserContactQuery,
    useUpsertEnsUserContactMutation,
    useListEnsUserContactsQuery,
    useUploadEnsAudioPromptMutation,
    useGenerateEnsTtsMutation,
    usePreviewEnsTtsMutation,
} = ensApi;
